<script setup lang="ts">
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  PhoneIcon,
  RotateCcwIcon,
  XCircleIcon,
} from 'lucide-vue-next';
import { computed, onMounted, ref } from 'vue';

import type { RingCentralConnection } from '@/types/ringcentral';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useRingCentralOAuth } from '@/composables/useRingCentral';
import { useOrganizationStore } from '@/stores/organization';

// ============ Meta & Route ============
// Note: This page uses blank layout and requires authentication

// ============ Reactive State ============

const status = ref<'processing' | 'success' | 'error'>('processing');
const processingMessage = ref('Connecting to RingCentral...');
const processingProgress = ref(0);
const countdown = ref(5);
const errorTitle = ref('');
const errorMessage = ref('');
const connectionDetails = ref<RingCentralConnection | null>(null);
const lastError = ref<any>(null);
const debugMode = ref(false);

// ============ Composables ============

const { startFlow } = useRingCentralOAuth();
const orgStore = useOrganizationStore();

// ============ Computed Properties ============

const urlParams = computed(() => {
  const params = new URLSearchParams(window.location.search);
  return {
    code: params.get('code'),
    state: params.get('state'),
    error: params.get('error'),
    error_description: params.get('error_description'),
  };
});

const statusIcon = computed(() => {
  switch (status.value) {
    case 'processing':
      return ClockIcon;
    case 'success':
      return CheckCircleIcon;
    case 'error':
      return XCircleIcon;
    default:
      return PhoneIcon;
  }
});

const statusStyles = computed(() => {
  switch (status.value) {
    case 'processing':
      return {
        iconBg: 'bg-orange-100',
        iconColor: 'text-orange-600',
      };
    case 'success':
      return {
        iconBg: 'bg-green-100',
        iconColor: 'text-green-600',
      };
    case 'error':
      return {
        iconBg: 'bg-red-100',
        iconColor: 'text-red-600',
      };
    default:
      return {
        iconBg: 'bg-gray-100',
        iconColor: 'text-gray-600',
      };
  }
});

const statusTitle = computed(() => {
  switch (status.value) {
    case 'processing':
      return 'Processing Connection';
    case 'success':
      return 'Successfully Connected!';
    case 'error':
      return 'Connection Failed';
    default:
      return 'RingCentral OAuth';
  }
});

const statusDescription = computed(() => {
  switch (status.value) {
    case 'processing':
      return 'Please wait while we establish your RingCentral connection...';
    case 'success':
      return 'Your RingCentral account has been successfully connected.';
    case 'error':
      return 'We encountered an issue connecting your RingCentral account.';
    default:
      return 'Processing OAuth callback...';
  }
});

// ============ Processing Logic ============

async function processOAuthCallback() {
  const params = urlParams.value;

  try {
    // Update progress and message
    updateProgress('Validating authorization...', 10);

    // Check for OAuth errors first
    if (params.error) {
      throw new Error(params.error_description || `OAuth error: ${params.error}`);
    }

    // Validate required parameters
    if (!params.code) {
      throw new Error('Missing authorization code. The OAuth flow may have been interrupted.');
    }

    if (!params.state) {
      throw new Error('Missing state parameter. This may indicate a security issue.');
    }

    updateProgress('Processing authorization code...', 30);

    // This page is no longer used since we switched to polling-based OAuth
    // Redirect to dashboard instead
    redirectToDashboard();
  } catch (error: any) {
    console.error('OAuth callback processing failed:', error);
    lastError.value = error;

    status.value = 'error';

    // Set appropriate error messages
    if (error.message?.includes('state')) {
      errorTitle.value = 'Security Verification Failed';
      errorMessage.value =
        'The authorization state could not be verified. Please try connecting again.';
    } else if (error.message?.includes('code')) {
      errorTitle.value = 'Authorization Code Invalid';
      errorMessage.value =
        'The authorization code is missing or invalid. Please try the connection process again.';
    } else if (error.message?.includes('expired')) {
      errorTitle.value = 'Authorization Expired';
      errorMessage.value =
        'The authorization session has expired. Please start the connection process again.';
    } else {
      errorTitle.value = 'Connection Error';
      errorMessage.value =
        error.message || 'An unexpected error occurred during the connection process.';
    }
  }
}

function updateProgress(message: string, progress: number) {
  processingMessage.value = message;
  processingProgress.value = progress;
}

// ============ Actions ============

function redirectToDashboard() {
  const currentOrg = orgStore.currentOrganization;
  if (currentOrg) {
    window.location.href = '/dashboard';
  } else {
    window.location.href = '/';
  }
}

function redirectNow() {
  redirectToDashboard();
}

function goToDashboard() {
  redirectToDashboard();
}

async function handleRetry() {
  if (!orgStore.currentOrganization) {
    goToDashboard();
    return;
  }

  try {
    // Clear any existing OAuth session
    sessionStorage.removeItem('ringcentral_oauth_state');
    sessionStorage.removeItem('ringcentral_oauth_orgId');

    // Start a new OAuth flow
    await startFlow(orgStore.currentOrganization.orgId);
  } catch (error) {
    console.error('Retry failed:', error);
    // If retry fails, go back to dashboard
    goToDashboard();
  }
}

// ============ Lifecycle ============

onMounted(async () => {
  // Enable debug mode with Ctrl+D
  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.ctrlKey && e.key === 'd') {
      debugMode.value = !debugMode.value;
      console.log('Debug mode:', debugMode.value ? 'enabled' : 'disabled');
    }
  };
  window.addEventListener('keydown', handleKeyPress);

  // Small delay to show the processing state
  await new Promise(resolve => setTimeout(resolve, 500));

  // Process the OAuth callback
  await processOAuthCallback();

  // Cleanup
  return () => {
    window.removeEventListener('keydown', handleKeyPress);
  };
});
</script>

<template>
  <div class="min-h-[100dvh] flex items-center justify-center bg-gray-50">
    <div class="max-w-md w-full mx-auto">
      <Card>
        <CardHeader class="text-center">
          <div
            class="mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-4"
            :class="statusStyles.iconBg"
          >
            <component :is="statusIcon" class="h-8 w-8" :class="statusStyles.iconColor" />
          </div>
          <CardTitle class="text-xl">
            {{ statusTitle }}
          </CardTitle>
          <CardDescription>{{ statusDescription }}</CardDescription>
        </CardHeader>

        <CardContent>
          <!-- Processing State -->
          <div v-if="status === 'processing'" class="text-center space-y-4">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto" />
            <div class="space-y-2">
              <p class="text-sm text-gray-600">
                {{ processingMessage }}
              </p>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div
                  class="bg-orange-500 h-2 rounded-full transition-all duration-1000"
                  :style="{ width: `${processingProgress}%` }"
                />
              </div>
            </div>
          </div>

          <!-- Success State -->
          <div v-else-if="status === 'success'" class="text-center space-y-4">
            <div class="bg-green-50 border border-green-200 rounded-lg p-4">
              <p class="text-sm text-green-800 font-medium">Connection Established!</p>
              <div v-if="connectionDetails" class="mt-2 text-xs text-green-700 space-y-1">
                <p>Organization: {{ connectionDetails.organizationId }}</p>
                <p>RingCentral Account: {{ connectionDetails.rcAccountId || 'Loading...' }}</p>
                <p>Extensions: {{ connectionDetails.rcExtensions?.length || 0 }}</p>
              </div>
            </div>

            <div class="text-sm text-gray-600">
              <p>Redirecting to dashboard in {{ countdown }} seconds...</p>
            </div>

            <Button class="w-full" @click="redirectNow">
              <ArrowRightIcon class="mr-2 h-4 w-4" />
              Go to Dashboard Now
            </Button>
          </div>

          <!-- Error State -->
          <div v-else-if="status === 'error'" class="space-y-4">
            <div class="bg-red-50 border border-red-200 rounded-lg p-4">
              <p class="text-sm font-medium text-red-800">
                {{ errorTitle }}
              </p>
              <p class="text-xs text-red-700 mt-1">
                {{ errorMessage }}
              </p>
            </div>

            <div class="space-y-2">
              <Button variant="outline" class="w-full" @click="handleRetry">
                <RotateCcwIcon class="mr-2 h-4 w-4" />
                Try Again
              </Button>
              <Button variant="ghost" class="w-full" @click="goToDashboard">
                <ArrowLeftIcon class="mr-2 h-4 w-4" />
                Return to Dashboard
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
</template>

<route lang="yaml">
meta:
  layout: blank
  auth: true
</route>
